import type { ReactNode } from "react";

// jsonHighlight.tsx
// Tiny JSON pretty-printer + colouriser for the payload / response viewers.
// It emits React nodes ONLY — never an HTML string — so a hostile response
// body from a merchant's server cannot inject markup into the dashboard.

export function prettyJson(raw: string): { text: string; isJson: boolean } {
  try { return { text: JSON.stringify(JSON.parse(raw), null, 2), isJson: true }; }
  catch { return { text: raw, isJson: false }; }
}

const MAX_HIGHLIGHT_CHARS = 120_000;
// 1 string, 2 trailing colon (=> key), 3 boolean, 4 null, 5 number, 6 punctuation
// Strings match first so numbers / `true` inside strings are never recoloured.
const TOKEN_RE =
  // eslint-disable-next-line no-useless-escape -- kept character-for-character as specified and tested
  /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false)\b|\b(null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}\[\],])/g;

export function highlightJson(src: string): ReactNode {
  if (src.length > MAX_HIGHLIGHT_CHARS) return src;
  const out: ReactNode[] = [];
  let last = 0;
  let k = 0;
  let m: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(src)) !== null) {
    if (m.index > last) out.push(src.slice(last, m.index));
    if (m[1] !== undefined) {
      if (m[2] !== undefined) out.push(<span key={k++} className="text-sky-300">{m[1]}</span>, m[2]);
      else out.push(<span key={k++} className="text-[#7BE88A]">{m[1]}</span>);
    } else if (m[3] !== undefined) out.push(<span key={k++} className="text-amber-300">{m[3]}</span>);
    // `null` and punctuation stay AA-readable on #0a0d12 — null is
    // meaningful in our payloads (paid_at: null).
    else if (m[4] !== undefined) out.push(<span key={k++} className="text-gray-400 italic">{m[4]}</span>);
    else if (m[5] !== undefined) out.push(<span key={k++} className="text-violet-300">{m[5]}</span>);
    else out.push(<span key={k++} className="text-gray-400">{m[6]}</span>);
    last = TOKEN_RE.lastIndex;
  }
  if (last < src.length) out.push(src.slice(last));
  return out;
}
